import React, { useEffect, useState } from 'react';
import Form from 'react-bootstrap/Form';
import { Button } from 'react-bootstrap';
import './prestormsurvey.css';
import { GetSurveyDetailsbyInstanceIdAsync, GetSurveyResponseAsync, SaveServeyResponse } from '../../../services/surveyService/SurveyService';
import { useAuth } from '../../../firebase/AuthProvider';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loader from '../loader/Loader';
import backarrowbtn from '../../../assets/images/backarrowbtn.svg'
import { EmergencyEventDto } from '../../../interfaces/survey/SurveyDtos';

const initialSurveyData = {
    evacuatedBefore: "",
    pastEvents: [] as string[],
    planToEvacuate: "",
    needHelpEvacuating: "",
    relocationInfo: {
        date: "",
        personName: "",
        relationship: "",
        locationAfterEvacuation: [] as string[],
        address: {
            street: "",
            apartment: "",
            city: "",
            state: "",
            zipCode: ""
        },
        contact: {
            homePhone: "",
            cellPhone: "",
            altPhone: ""
        },
        description: ""
    }
};

const Prestormsurvey: React.FC = () => {
    const { currentUser } = useAuth();
    const { participantId, instanceId } = useParams();
    const [surveyData, setSurveyData] = useState<typeof initialSurveyData>(initialSurveyData);
    const [authToken, setAuthToken] = useState<string>();
    const [loading, setLoading] = useState<boolean>(false);
    const [isAlreadyFilled, setIsAllreadyFilled] = useState<boolean>(false);
    const [validated, setValidated] = useState(false);
    const [surveyDetails, setSurveyDetails] = useState<EmergencyEventDto>();
    const navigate = useNavigate();

    useEffect(() => {
        currentUser?.getIdToken().then((token) => {
            if (token) {
                setAuthToken(token);
                if (instanceId) {
                    GetSurveyResponse(token, instanceId);
                    GetSurveyDetailsByInstanceId(token, instanceId);
                }
            }
        })
    }, []);

    const GetSurveyDetailsByInstanceId = ( token : string, instance_Id : string) =>{
        GetSurveyDetailsbyInstanceIdAsync(token, instance_Id).then((res)=>{
            if(res.success){
                setSurveyDetails(res.data);
            }else{
                toast.error(res.message);
            }
        }).catch((err)=>{
                toast.error(err.message);
        })
    }

    const GetSurveyResponse = (token: string, instance_Id: string) => {
        setLoading(true);
        GetSurveyResponseAsync(token, instance_Id).then((res) => {
            if (res.success) {
                const surveyData = JSON.parse(res.data.responseData);
                setSurveyData(surveyData)
                setIsAllreadyFilled(true);
            }
        }).finally(() => {
            setLoading(false);
        })
    }

    // Handlers for top-level fields
    const handleRadio = (key: keyof typeof initialSurveyData, value: string) => {
        setSurveyData(prev => ({
            ...prev,
            [key]: value
        }));
    };

    // Handler for pastEvents (array)
    const handlePastEvents = (event: string, checked: boolean) => {
        setSurveyData(prev => ({
            ...prev,
            pastEvents: checked
                ? [...prev.pastEvents, event]
                : prev.pastEvents.filter(e => e !== event)
        }));
    };

    // Handler for relocationInfo fields
    const handleRelocationInfo = (key: keyof typeof initialSurveyData.relocationInfo, value: any) => {
        setSurveyData(prev => ({
            ...prev,
            relocationInfo: {
                ...prev.relocationInfo,
                [key]: value
            }
        }));
    };

    // Handler for relocationInfo.locationAfterEvacuation (array)
    const handleLocationAfterEvacuation = (location: string, checked: boolean) => {
        setSurveyData(prev => ({
            ...prev,
            relocationInfo: {
                ...prev.relocationInfo,
                locationAfterEvacuation: checked
                    ? [...prev.relocationInfo.locationAfterEvacuation, location]
                    : prev.relocationInfo.locationAfterEvacuation.filter(l => l !== location)
            }
        }));
    };

    // Handler for relocationInfo.address fields
    const handleAddress = (key: keyof typeof initialSurveyData.relocationInfo.address, value: string) => {
        setSurveyData(prev => ({
            ...prev,
            relocationInfo: {
                ...prev.relocationInfo,
                address: {
                    ...prev.relocationInfo.address,
                    [key]: value
                }
            }
        }));
    };

    // Handler for relocationInfo.contact fields
    const handleContact = (key: keyof typeof initialSurveyData.relocationInfo.contact, value: string) => {
        setSurveyData(prev => ({
            ...prev,
            relocationInfo: {
                ...prev.relocationInfo,
                contact: {
                    ...prev.relocationInfo.contact,
                    [key]: value
                }
            }
        }));
    };

    // Validation helpers
    const isFieldEmpty = (value: any) => {
        if (Array.isArray(value)) return value.length === 0;
        return !value;
    };

    const validateSurvey = () => {
        if (!surveyData.evacuatedBefore) return false;
        if (!surveyData.needHelpEvacuating) return false;
        if (!surveyData.planToEvacuate) return false;
        if (surveyData.evacuatedBefore === "Yes" && surveyData.pastEvents.length === 0) return false;
        if (surveyData.planToEvacuate === "Yes") {
            const r = surveyData.relocationInfo;
            if (
                !r.date ||
                !r.personName ||
                !r.relationship ||
                r.locationAfterEvacuation.length === 0 ||
                !r.address.street ||
                !r.address.apartment ||
                !r.address.city ||
                !r.address.state ||
                !r.address.zipCode ||
                !r.contact.homePhone ||
                !r.contact.cellPhone ||
                !r.contact.altPhone ||
                !r.description
            ) {
                return false;
            }
        }
        return true;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setValidated(true);
        if (!authToken || !instanceId) return;
        if (!validateSurvey()) {
            toast.error("Please fill all required fields.");
            return;
        }
        setLoading(true);
        SaveServeyResponse(authToken, surveyData, instanceId).then((res) => {
            if (res.success) {
                toast.success(res.message);
                navigate(`/surveys/participant/${participantId}`)
            } else {
                toast.error(res.message);
            }
        }).catch((err) => {
            toast.error(err.message);
        }).finally(() => {
            setLoading(false);
        })
    };

     // FLUSH subfields if "No" is selected for evacuatedBefore or planToEvacuate
    useEffect(() => {
        if (surveyData.evacuatedBefore === "No") {
            setSurveyData(prev => ({
                ...prev,
                pastEvents: [],
            }));
        }
        if (surveyData.planToEvacuate === "No") {
            setSurveyData(prev => ({
                ...prev,
                relocationInfo: {
                    date: "",
                    personName: "",
                    relationship: "",
                    locationAfterEvacuation: [],
                    address: {
                        street: "",
                        apartment: "",
                        city: "",
                        state: "",
                        zipCode: ""
                    },
                    contact: {
                        homePhone: "",
                        cellPhone: "",
                        altPhone: ""
                    },
                    description: ""
                }
            }));
        }
    }, [surveyData.evacuatedBefore, surveyData.planToEvacuate]);

    return (
        <>
            {loading && <Loader />}
            <div className="prestorm_survey_conatiner">
                <Form onSubmit={handleSubmit}>
                    <div className="common_card">
                        <div className="top border_tittle mby_16">
                            <div className="top_bar_tittle">
                                <h2> <Link to={`/surveys/participant/${participantId}`}><img src={backarrowbtn} alt="" /></Link> {surveyDetails?.emergencyType}</h2>
                               
                            </div>
                             <p> EmergencyDetail : {surveyDetails?.emergencyDetail}</p>
                        </div>
                        <div className="evacuation_plans_head mby_16">
                            <h3>Evacuation Plans</h3>
                        </div>
                        <Form.Group className="step_form_grp" controlId="Q1">
                            <Form.Label className='required-label'>
                                1. Did you evacuate for a previous event?
                            </Form.Label>
                            <div className="check_list_yes_no">
                                <Form.Check
                                    type="radio"
                                    id="Q1-yes"
                                    name="Q1"
                                    label="Yes"
                                    className='check_with_label_two'
                                    checked={surveyData.evacuatedBefore === "Yes"}
                                    onChange={() => handleRadio("evacuatedBefore", "Yes")}
                                    disabled={isAlreadyFilled}
                                />
                                <Form.Check
                                    type="radio"
                                    id="Q1-no"
                                    name="Q1"
                                    label="No"
                                    className='check_with_label_two'
                                    checked={surveyData.evacuatedBefore === "No"}
                                    onChange={() => handleRadio("evacuatedBefore", "No")}
                                    disabled={isAlreadyFilled}
                                />
                            </div>
                        </Form.Group>
                        {
                            surveyData.evacuatedBefore === "Yes" &&
                            (
                                <Form.Group className="step_form_grp mby_t_16" controlId="Q1WE">
                                    <Form.Label className='required-label'>
                                        Which event?
                                    </Form.Label>
                                    <div className="check_list_flx flex-column">
                                        {["Hurricane Ida", "Hurricane Laura", "Hurricane Delta", "2024 Ice Storm", "Other"].map(event => (
                                            <Form.Check
                                                key={event}
                                                type="checkbox"
                                                id={`Q1WE-${event}`}
                                                name="Q1WE"
                                                label={event}
                                                className='check_with_label_two'
                                                checked={surveyData.pastEvents.includes(event)}
                                                onChange={e => handlePastEvents(event, e.target.checked)}
                                                disabled={isAlreadyFilled}
                                            />
                                        ))}
                                    </div>
                                </Form.Group>
                            )
                        }
                    </div>
                    <div className="common_card">
                        <Form.Group className="step_form_grp" controlId="Q3">
                            <Form.Label className='required-label'>
                                2. Will you need help evacuating?
                            </Form.Label>
                            <div className="check_list_yes_no">
                                <Form.Check
                                    type="radio"
                                    id="Q3-yes"
                                    name="Q3"
                                    label="Yes"
                                    className='check_with_label_two'
                                    checked={surveyData.needHelpEvacuating === "Yes"}
                                    onChange={() => handleRadio("needHelpEvacuating", "Yes")}
                                    disabled={isAlreadyFilled}
                                />
                                <Form.Check
                                    type="radio"
                                    id="Q3-no"
                                    name="Q3"
                                    label="No"
                                    className='check_with_label_two'
                                    checked={surveyData.needHelpEvacuating === "No"}
                                    onChange={() => handleRadio("needHelpEvacuating", "No")}
                                    disabled={isAlreadyFilled}
                                />
                            </div>
                        </Form.Group>
                    </div>
                    <div className="common_card">
                        <Form.Group className="step_form_grp" controlId="Q2">
                            <Form.Label className='required-label'>
                                3. Do you plan to evacuate for this storm?
                            </Form.Label>
                            <div className="check_list_yes_no">
                                <Form.Check
                                    type="radio"
                                    id="Q2-yes"
                                    name="Q2"
                                    label="Yes"
                                    className='check_with_label_two'
                                    checked={surveyData.planToEvacuate === "Yes"}
                                    onChange={() => handleRadio("planToEvacuate", "Yes")}
                                    disabled={isAlreadyFilled}
                                />
                                <Form.Check
                                    type="radio"
                                    id="Q2-no"
                                    name="Q2"
                                    label="No"
                                    className='check_with_label_two'
                                    checked={surveyData.planToEvacuate === "No"}
                                    onChange={() => handleRadio("planToEvacuate", "No")}
                                    disabled={isAlreadyFilled}
                                />
                            </div>
                        </Form.Group>

                        {surveyData.planToEvacuate === "Yes" &&
                            (
                                <>
                                    <div className="top border_tittle mby_t_16">
                                        <div className="top_bar_tittle">
                                            <h2>Relocation Information</h2>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <Form.Group className="custom_input_flled" controlId="relocation-date">
                                                <Form.Label className='required-label'>
                                                    Select Date
                                                </Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    placeholder="Select date"
                                                    value={surveyData.relocationInfo.date}
                                                    onChange={e => handleRelocationInfo("date", e.target.value)}
                                                    disabled={isAlreadyFilled}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-6">
                                            <Form.Group className="custom_input_flled" controlId="relocation-personName">
                                                <Form.Label className='required-label'>
                                                    Name of Person
                                                </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter name"
                                                    value={surveyData.relocationInfo.personName}
                                                    onChange={e => handleRelocationInfo("personName", e.target.value)}
                                                    disabled={isAlreadyFilled}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-6">
                                            <Form.Group className="custom_input_flled" controlId="relocation-relationship">
                                                <Form.Label className='required-label'>
                                                    Relationship of Person
                                                </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter relationship"
                                                    value={surveyData.relocationInfo.relationship}
                                                    onChange={e => handleRelocationInfo("relationship", e.target.value)}
                                                    disabled={isAlreadyFilled}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-6">
                                            <Form.Group className="step_form_grp" controlId="relocation-location">
                                                <Form.Label className='required-label'>
                                                    Current location after evacuation
                                                </Form.Label>
                                                <div className="check_list_flx flex-column">
                                                    {["Family / Friends", "Direct Care Staff", "Hotel", "Facility", "Rental", "Home", "Shelter", "FEMA MHU"].map(location => (
                                                        <Form.Check
                                                            key={location}
                                                            type="checkbox"
                                                            id={`location-${location}`}
                                                            name="location"
                                                            label={location}
                                                            className='check_with_label_two'
                                                            checked={surveyData.relocationInfo.locationAfterEvacuation.includes(location)}
                                                            onChange={e => handleLocationAfterEvacuation(location, e.target.checked)}
                                                            disabled={isAlreadyFilled}
                                                        />
                                                    ))}
                                                </div>
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-6">
                                            <Form.Group className="custom_input_flled" controlId="relocation-address">
                                                <Form.Label className='required-label'>
                                                    Relocation Address
                                                </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter address"
                                                    value={surveyData.relocationInfo.address.street}
                                                    onChange={e => handleAddress("street", e.target.value)}
                                                    disabled={isAlreadyFilled}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-6">
                                            <Form.Group className="custom_input_flled" controlId="relocation-apartment">
                                                <Form.Label className='required-label'>
                                                    Apartment
                                                </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter Apt"
                                                    value={surveyData.relocationInfo.address.apartment}
                                                    onChange={e => handleAddress("apartment", e.target.value)}
                                                    disabled={isAlreadyFilled}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-6">
                                            <Form.Group className="custom_input_flled" controlId="relocation-city">
                                                <Form.Label className='required-label'>
                                                    City
                                                </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter city"
                                                    value={surveyData.relocationInfo.address.city}
                                                    onChange={e => handleAddress("city", e.target.value)}
                                                    disabled={isAlreadyFilled}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-6">
                                            <Form.Group className="custom_input_flled" controlId="relocation-state">
                                                <Form.Label className='required-label'>
                                                    State
                                                </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter state"
                                                    value={surveyData.relocationInfo.address.state}
                                                    onChange={e => handleAddress("state", e.target.value)}
                                                    disabled={isAlreadyFilled}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-6">
                                            <Form.Group className="custom_input_flled" controlId="relocation-zip">
                                                <Form.Label className='required-label'>
                                                    Zip Code
                                                </Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    placeholder="Enter zip code"
                                                    value={surveyData.relocationInfo.address.zipCode}
                                                    onChange={e => handleAddress("zipCode", e.target.value)}
                                                    disabled={isAlreadyFilled}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-6">
                                            <Form.Group className="custom_input_flled" controlId="relocation-homePhone">
                                                <Form.Label className='required-label'>
                                                    Home Phone
                                                </Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    placeholder="Enter number"
                                                    value={surveyData.relocationInfo.contact.homePhone}
                                                    onChange={e => handleContact("homePhone", e.target.value)}
                                                    disabled={isAlreadyFilled}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-6">
                                            <Form.Group className="custom_input_flled" controlId="relocation-cellPhone">
                                                <Form.Label className='required-label'>
                                                    Cell Phone
                                                </Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    placeholder="Enter number"
                                                    value={surveyData.relocationInfo.contact.cellPhone}
                                                    onChange={e => handleContact("cellPhone", e.target.value)}
                                                    disabled={isAlreadyFilled}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-6">
                                            <Form.Group className="custom_input_flled" controlId="relocation-altPhone">
                                                <Form.Label className='required-label'>
                                                    Alternative Contact
                                                </Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    placeholder="Enter number"
                                                    value={surveyData.relocationInfo.contact.altPhone}
                                                    onChange={e => handleContact("altPhone", e.target.value)}
                                                    disabled={isAlreadyFilled}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="col-md-12">
                                            <Form.Group className="custom_input_flled mb-0" controlId="relocation-description">
                                                <Form.Label className='required-label'>
                                                    Add Description
                                                </Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={3}
                                                    placeholder="Enter description"
                                                    value={surveyData.relocationInfo.description}
                                                    onChange={e => handleRelocationInfo("description", e.target.value)}
                                                    disabled={isAlreadyFilled}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                </>
                            )}
                        <div className='mby_t_24'>
                            <Button variant='primary' className='w-100' type="submit" disabled={isAlreadyFilled}>Submit</Button>
                        </div>
                    </div>
                </Form>
            </div>
        </>
    );
};

export default Prestormsurvey;